import React, { useCallback, useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownLeft, CheckCircle2, X, Loader2, User } from "lucide-react";
import { fmtDateTime } from "@/lib/format";

const TABS = [
  { key: "all", label: "All" },
  { key: "outward", label: "Outward" },
  { key: "inward", label: "Inward" },
];

export default function Referrals() {
  const [dir, setDir] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/referrals", { params: { direction: dir, page_size: 50 } });
      setRows(data || []);
    } catch { toast.error("Failed to load referrals"); }
    finally { setLoading(false); }
  }, [dir]);

  useEffect(() => { load(); }, [load]);

  const act = async (r, action) => {
    setBusyId(r.id);
    try {
      await api.post(`/referrals/${r.id}/${action}`);
      toast.success(`Referral ${action}ed`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-widest text-[#8A9992]">Care circle</div>
        <h1 className="text-3xl font-semibold text-[#1F2A24] mt-1" style={{ fontFamily: "Instrument Sans" }}>Referrals</h1>
      </div>

      <div className="card-soft p-2 md:p-3 mb-4 inline-flex gap-1 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setDir(t.key)} data-testid={`referral-tab-${t.key}`}
            className={`pill-tab ${dir === t.key ? "pill-tab-active" : "pill-tab-inactive"}`}>{t.label}</button>
        ))}
      </div>

      <div className="card-soft p-2 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-[#8A9992]"><Loader2 className="animate-spin inline mr-2" size={14} />Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-[#8A9992]">No referrals in this filter.</div>
        ) : (
          <div className="divide-y divide-[#EFECE5]">
            {rows.map((r) => {
              const isInward = r.direction === "inward";
              const Icon = isInward ? ArrowDownLeft : ArrowUpRight;
              return (
                <div key={r.id} className="flex items-center gap-4 px-4 py-4" data-testid={`referral-${r.id}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-none ${isInward ? "bg-[#E6F0EC] text-[#0A4C36]" : "bg-[#FCE7D9] text-[#8A5A34]"}`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#1F2A24]">{r.patient_name || "Patient"}</span>
                      <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-white border border-[#EFECE5] text-[#5C7568]">{isInward ? "Inward" : "Outward"}</span>
                      {r.speciality && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#E6F0EC] text-[#0A4C36]">{r.speciality}</span>}
                    </div>
                    <div className="text-xs text-[#5C7568] mt-1 inline-flex items-center gap-1">
                      <User size={11} /> {isInward ? `From ${r.doctor_name}` : `To ${r.to_doctor_name}`}
                      <span className="text-[#8A9992] mx-1">·</span>
                      {fmtDateTime(r.created_at)}
                    </div>
                    {r.note && <div className="text-xs text-[#47554D] mt-1 italic">&ldquo;{r.note}&rdquo;</div>}
                    {r.share_history && <div className="text-[10px] text-[#0A4C36] mt-1">✓ Patient history shared with consent</div>}
                  </div>
                  <div>
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${
                      r.status === "pending" ? "bg-[#FCE7D9] text-[#8A5A34]"
                      : r.status === "accepted" ? "bg-[#E6F0EC] text-[#0A4C36]"
                      : "bg-[#F8E7DF] text-[#B14A2A]"
                    }`}>{r.status}</span>
                  </div>
                  {isInward && r.status === "pending" && (
                    <div className="flex gap-2">
                      <button onClick={() => act(r, "accept")} disabled={busyId === r.id} className="btn-emerald text-xs" data-testid={`ref-accept-${r.id}`}>
                        {busyId === r.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Accept
                      </button>
                      <button onClick={() => act(r, "decline")} disabled={busyId === r.id} className="btn-outline-emerald text-xs" data-testid={`ref-decline-${r.id}`}>
                        <X size={12} /> Decline
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
